import { IonCol, IonGrid, IonLoading, IonRow } from '@ionic/react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AuthUserInfo } from 'src/redux/auth/types';
import { selectLogLocalDataByFilename } from 'src/redux/log/selectors';
import { useSocketList } from 'src/utils/socket';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { GenericMessages } from '../../intl/GenericMessages';
import { HomeMessages } from '../../intl/HomeMessages';
import { JobMessages } from '../../intl/JobMessages';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { DqText } from '../dq-text';
import { ChipType } from '../reusable-components/Chip';
import MobileJobCard from '../reusable-components/mobile-job-card/MobileJobCard';
import './MobileRecentActivityContainer.scss';
import { AppRouteConstants } from '../../pages/app-layout/AppRouteConstants';

const MobileRecentActivityContainer: React.FC<{ userData?: AuthUserInfo }> = props => {
  const { formatMessage } = useIntl();

  /** A dictionary of log files on the local device. */
  const localLogFiles = useSelector(selectLogLocalDataByFilename);

  const logsQuery = React.useMemo(() => {
    return {
      query: { org: props.userData?.org },
      paginate: { sort: { createdAt: -1 }, limit: 3 },
    };
  }, [props.userData?.org]);

  const userQuery = React.useMemo(() => {
    return {
      query: { org: props.userData?.org },
      paginate: { sort: { createdAt: -1 }, limit: 1000 },
    };
  }, [props.userData?.org]);

  const {
    items: recentLogs,
    isLoading: logsLoading,
    fetched: logsFetched,
  } = useSocketList(SocketContextSentinel, 'logs', logsQuery);
  const {
    items: users,
    isLoading: usersLoading,
    fetched: usersFetched,
  } = useSocketList(SocketContextPortal, 'users', userQuery);

  const logs = React.useMemo(() => {
    const localLogFilesList = Object.values(localLogFiles);
    const localLogs = localLogFilesList.map(log => {
      const remoteMatch = recentLogs.find(
        r => r.originalFileName === log.filename && r.serialNumber === log.serialNumber,
      );

      return {
        routerLink: `/jobs/file/${log.filename}`,
        createdAt: log.createdAt,
        timezone: remoteMatch?.deviceTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        // Checks both undefined or empty string here to determine displaying description or filename
        title: log?.metadata?.description || remoteMatch?.description || log.filename.replace(/\.log$/i, ''),
        createdBy:
          props.userData?.givenName && props.userData?.familyName
            ? `${props.userData.givenName} ${props.userData.familyName}`
            : formatMessage(GenericMessages.unknownPlaceholder),
        linesParsed: log.raw.split('\n').length,
        chip: !!log.pendingClose
          ? { title: formatMessage(JobMessages.jobComplete), type: ChipType.JOB_COMPLETE }
          : { title: formatMessage(JobMessages.jobLive), type: ChipType.JOB_LIVE },
        isLocal: true,
      };
    });
    const remoteLogs = recentLogs
      .filter(log => {
        if (localLogFilesList.some(k => k.filename === log.originalFileName) && log.status === 'in-progress')
          return false;
        return true;
      })
      .map(log => {
        const user = users.find(u => log.createdBy === u._id);
        const userName = !user
          ? formatMessage(GenericMessages.unknownPlaceholder)
          : `${user.given_name} ${user.family_name}`;
        return {
          routerLink: AppRouteConstants.LogDetails.replace(':id', log._id),
          createdAt: log.createdAt,
          timezone: log.deviceTimezone,
          // Checks both undefined or empty string here to determine displaying description or filename
          title: log.description || log.name,
          createdBy: userName,
          linesParsed: log.linesParsed,
          chip:
            log.status === 'in-progress'
              ? { type: ChipType.JOB_INPROGRESS, title: formatMessage(JobMessages.jobInProgress) }
              : { type: ChipType.JOB_COMPLETE, title: formatMessage(JobMessages.jobComplete) },
          isLocal: false,
        };
      });
    return [...localLogs, ...remoteLogs].slice(0, 3);
  }, [formatMessage, localLogFiles, props.userData?.familyName, props.userData?.givenName, recentLogs, users]);

  return (
    <div className="recent-activity-container">
      <div className="recent-activity-header">
        <IonGrid>
          <IonRow>
            <IonCol>
              <DqText variant="h3">
                <FormattedMessage {...HomeMessages.recentActivity} />
              </DqText>
            </IonCol>
            <IonCol>
              <Link to={AppRouteConstants.LogsList}>
                <FormattedMessage {...HomeMessages.viewAllJobs} />
              </Link>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      <IonLoading
        isOpen={!logsFetched && !usersFetched && (usersLoading || logsLoading)}
        duration={1000}
        spinner="circles"
      />
      {!!logs.length && !!users ? (
        logs.map(log => {
          return (
            <MobileJobCard
              routerLink={log.routerLink}
              createdAt={log.createdAt}
              timezone={log.timezone}
              title={log.title}
              createdByName={log.createdBy}
              key={log.routerLink}
              linesParsed={log.linesParsed}
              chipProps={log.chip}
              isLocal={log.isLocal}
            />
          );
        })
      ) : (
        <FormattedMessage {...JobMessages.recentActivityNoJobsRecorded} />
      )}
    </div>
  );
};

export default MobileRecentActivityContainer;
